/**
 * The client panel opened from a card or a table row — the prototype's client page (`VIEWS.client`
 * overview) as a side panel: who they are, why this priority, account facts with the SPOC and its
 * "Change" link, contacts, products and the dated SPOC history.
 *
 * Data: GET /clients/{id} (account, contacts, products, priority suggestion),
 * GET /clients/{id}/health (step 6: the score, its band and one reason line per penalty — prototype `hchip()` and
 * "Why the health reads …" at L1272) and GET /clients/{id}/spocs (history, newest first). Revenue lines show
 * only when the field is present.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { HEALTH_BAND_LABEL, type ClientDetail, type ClientHealthDetail, type ReasonBand, type SpocAssignment } from '@amplispot/contracts';
import { useSession } from '../../lib/session';
import { useClient, useSpocHistory } from '../../lib/data';
import { UNSHOWN_REASONS_LINE, scoreHasUnshownReasons, useClientHealth } from '../../lib/health';
import { dayMonth } from '../../lib/timesheets';
import { monthYear, rupees } from '../../lib/format';
import { Avatar, Drawer, Empty, ErrorState, HealthChip, Loading, PriorityChip } from '../../ui/kit';
import { ClientTasksDue, ClientToDo } from './ClientToDo';
import { ChangeSpocModal } from '../ChangeSpocModal';
import { EditPriorityModal } from './ClientModals';
import { ClientLogo } from './parts';
import { Lbl } from '../../shell/Help';
import { ClientMeetings, LogMeetingModal } from './Meetings';

const PRODUCT_STATUS = { active: 'Active', paused: 'Paused', ended: 'Ended' } as const;

export function ClientDrawer({
  clientId,
  onClose,
  tab,
  onOpenMeeting,
}: {
  clientId: string;
  onClose: () => void;
  /** `meetings` (the deep link on tasks made from minutes) scrolls to the client's meetings. */
  tab?: string | null;
  onOpenMeeting?: (id: string) => void;
}) {
  const { can, me, readOnly } = useSession();
  const detail = useClient(clientId);
  const [modal, setModal] = useState<'prio' | 'spoc' | 'meeting' | null>(null);
  const canEdit = can('clients.edit.all');
  const work = !!detail.data && (can('tasks.read.all') || detail.data.spoc.id === me.user.id);
  // Prototype client head "Make client deck": managers, or the client's current SPOC (POST /clients/{id}/decks).
  const deck = !!detail.data && (can('clients.read.all') || detail.data.spoc.id === me.user.id);
  const nav = useNavigate();

  return (
    <>
      <Drawer
        title="Client"
        onClose={onClose}
        footer={
          <>
            <div className="grow" />
            <button className="btn" onClick={onClose}>
              Close
            </button>
            {work && !readOnly ? (
              <button className="btn" onClick={() => setModal('meeting')}>
                Log a meeting
              </button>
            ) : null}
            {deck ? (
              <button className="btn" onClick={() => nav(`/clients/deck?client=${encodeURIComponent(clientId)}`)}>
                Make client deck
              </button>
            ) : null}
            {canEdit && detail.data ? (
              <button className="btn pri" onClick={() => setModal('prio')}>
                Edit priority and revenue
              </button>
            ) : null}
          </>
        }
      >
        {detail.isPending ? (
          <Loading label="Loading the client…" />
        ) : detail.isError ? (
          <ErrorState error={detail.error} onRetry={() => detail.refetch()} />
        ) : (
          <Body
            c={detail.data}
            canEdit={canEdit}
            canChangeSpoc={can('client.changeSpoc')}
            onEdit={() => setModal('prio')}
            onChangeSpoc={() => setModal('spoc')}
            tab={tab ?? null}
            onOpenMeeting={onOpenMeeting}
          />
        )}
      </Drawer>
      {modal === 'prio' ? <EditPriorityModal clientId={clientId} onClose={() => setModal(null)} /> : null}
      {modal === 'spoc' && detail.data ? <ChangeSpocModal client={detail.data} onClose={() => setModal(null)} /> : null}
      {modal === 'meeting' ? <LogMeetingModal clientId={clientId} onClose={() => setModal(null)} onSubmitted={onOpenMeeting} /> : null}
    </>
  );
}

function Body(props: {
  c: ClientDetail;
  canEdit: boolean;
  canChangeSpoc: boolean;
  onEdit: () => void;
  onChangeSpoc: () => void;
  tab: string | null;
  onOpenMeeting?: (id: string) => void;
}) {
  const { c } = props;
  const primary = c.contacts.find((x) => x.is_primary) ?? c.contacts[0];
  const live = c.products.filter((p) => p.status === 'active');
  const health = useClientHealth(c.id);
  const { me, can } = useSession();
  // Prototype Client 360: the step-3 sections are for managers and the client's SPOC.
  const work = can('tasks.read.all') || c.spoc.id === me.user.id;
  return (
    <>
      <div className="split" style={{ gap: 14, marginBottom: 14, alignItems: 'center' }}>
        <ClientLogo id={c.id} name={c.name} size={48} />
        <div style={{ minWidth: 0 }}>
          <h2 style={{ fontSize: 19, lineHeight: 1.25 }}>{c.name}</h2>
          <div className="sub" style={{ fontSize: 12.5 }}>
            {c.segment}, since {monthYear(c.since)}
            {live.length ? ` · ${live.map((p) => p.product_name).join(', ')}` : ''}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
        <PriorityChip p={c.priority} onClick={props.canEdit ? props.onEdit : undefined} />
        {health.data ? <HealthChip band={health.data.band} score={health.data.value} /> : null}
        {c.strategic ? <span className="chip brand">Strategic account</span> : null}
        {c.is_new_client ? <span className="chip info">New client</span> : null}
        {c.status === 'archived' ? <span className="chip">Archived</span> : null}
      </div>

      <h3><Lbl>Why this priority</Lbl></h3>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {c.priority_suggestion.reasons.length ? (
          c.priority_suggestion.reasons.map((r) => (
            <span key={r} className="chip">
              {r}
            </span>
          ))
        ) : (
          <span className="sub">No special reason, standard service.</span>
        )}
      </div>
      {c.priority_note ? (
        <div className="note" style={{ marginTop: 12, marginBottom: 0 }}>
          <b>Note for the team:</b> {c.priority_note}
        </div>
      ) : null}

      {work ? <ClientToDo clientId={c.id} /> : null}

      <HealthWhy q={health} />

      <h3>Account</h3>
      <dl className="kv">
        <dt>Segment</dt>
        <dd>{c.segment}</dd>
        <dt>Client since</dt>
        <dd>{monthYear(c.since)}</dd>
        <dt>Main contact</dt>
        <dd>{primary ? `${primary.name}, ${primary.title}` : <span className="sub">None recorded</span>}</dd>
        <dt>SPOC</dt>
        <dd>
          {c.spoc.name}
          {props.canChangeSpoc ? (
            <button type="button" className="lnk" onClick={props.onChangeSpoc} style={{ fontSize: 12, marginLeft: 6 }}>
              Change
            </button>
          ) : null}
        </dd>
        <dt>Ops hours</dt>
        <dd className="num">{c.monthly_hours} a month</dd>
        {c.annual_revenue_lakh !== undefined ? (
          <>
            <dt>Annual revenue</dt>
            <dd className="num">{c.annual_revenue_lakh === null ? <span className="sub">Not set</span> : `${rupees(c.annual_revenue_lakh)} a year`}</dd>
          </>
        ) : null}
      </dl>

      {work ? <ClientTasksDue clientId={c.id} /> : null}

      {work && props.onOpenMeeting ? <MeetingsSection clientId={c.id} focus={props.tab === 'meetings'} onOpen={props.onOpenMeeting} /> : null}

      <h3>Contacts</h3>
      <div className="panel">
        {c.contacts.length ? (
          c.contacts.map((x) => (
            <div className="li" key={x.id}>
              <div className="b">
                <div className="t">
                  {x.name} {x.is_primary ? <span className="chip brand" style={{ marginLeft: 6 }}>Main contact</span> : null}
                </div>
                <div className="m">
                  <span>{x.title}</span>
                  {x.email ? <a href={`mailto:${x.email}`} className="lnk">{x.email}</a> : null}
                  {x.phone ? <span className="num">{x.phone}</span> : null}
                </div>
              </div>
            </div>
          ))
        ) : (
          <Empty title="No contacts yet">Contacts added for this client show here.</Empty>
        )}
      </div>

      <h3>Products</h3>
      <div className="panel">
        {c.products.length ? (
          c.products.map((p) => (
            <div className="li" key={p.id}>
              <div className="b">
                <div className="t">{p.product_name}</div>
                <div className="m">
                  {p.started_on ? <span>Since {monthYear(p.started_on)}</span> : null}
                  {p.mrr_lakh != null ? <span className="num">{rupees(p.mrr_lakh)} a month</span> : null}
                </div>
              </div>
              <span className={`chip${p.status === 'active' ? ' hot' : p.status === 'paused' ? ' warm' : ''}`}>{PRODUCT_STATUS[p.status]}</span>
            </div>
          ))
        ) : (
          <Empty title="No products yet">Products this client buys show here.</Empty>
        )}
      </div>

      <SpocHistoryList clientId={c.id} />
    </>
  );
}

/** The prototype Client 360 "Meetings" tab, as a section of the client panel. */
function MeetingsSection({ clientId, focus, onOpen }: { clientId: string; focus: boolean; onOpen: (id: string) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (focus) ref.current?.scrollIntoView({ block: 'start' });
  }, [focus]);
  return (
    <div ref={ref} id="client-meetings">
      <h3>Meetings</h3>
      <ClientMeetings clientId={clientId} onOpen={onOpen} />
    </div>
  );
}

function SpocHistoryList({ clientId }: { clientId: string }) {
  const h = useSpocHistory(clientId);
  return (
    <>
      <h3>SPOC history</h3>
      <div className="panel">
        {h.isPending ? (
          <Loading label="Loading the history…" />
        ) : h.isError ? (
          <ErrorState error={h.error} onRetry={() => h.refetch()} />
        ) : h.data.data.length ? (
          h.data.data.map((a) => <SpocRow key={a.id} a={a} />)
        ) : (
          <Empty title="No SPOC recorded yet" />
        )}
      </div>
    </>
  );
}

function SpocRow({ a }: { a: SpocAssignment }) {
  return (
    <div className="li">
      <Avatar user={a.user} size={26} />
      <div className="b">
        <div className="t">
          {a.user.name} {a.is_current ? <span className="chip hot" style={{ marginLeft: 6 }}>Now</span> : null}
        </div>
        <div className="m">
          <span>
            {monthYear(a.from_date)} to {a.to_date ? monthYear(a.to_date) : 'now'}
          </span>
          {a.reason ? <span>{a.reason}</span> : null}
          {a.changed_by ? <span>Changed by {a.changed_by.name}</span> : null}
        </div>
      </div>
    </div>
  );
}

/** Reason band → the prototype's dot colour (green good news, amber a warning, red a serious one). */
const DOT: Record<ReasonBand, string> = { ok: 'hot', warn: 'warm', bad: 'cold' };

/** Prototype VIEWS.client overview: "Why the health reads healthy / watch / at risk" — one line per reason. */
function HealthWhy({ q }: { q: ReturnType<typeof useClientHealth> }) {
  const { can } = useSession();
  if (q.isPending)
    return (
      <>
        <h3>Health</h3>
        <Loading label="Working out the health score…" />
      </>
    );
  if (q.isError)
    return (
      <>
        <h3>Health</h3>
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      </>
    );
  const h: ClientHealthDetail = q.data;
  // Without toneReason.read the tone lines are left out but their points stay in the score (review t-2d1f14a2 #7).
  const unshown = !can('toneReason.read') && scoreHasUnshownReasons(h);
  return (
    <>
      <h3>
        <Lbl>{`Why the health reads ${HEALTH_BAND_LABEL[h.band].toLowerCase()}`}</Lbl>
      </h3>
      <div className="panel">
        {h.explain.length ? (
          h.explain.map((w, i) => (
            <div className="li" key={`${w.signal}-${i}`}>
              <span className={`dot ${DOT[w.band]}`} style={{ marginTop: 6 }} />
              <div className="b">{w.detail}</div>
              {w.source === 'mail' ? <span className="chip info">From emails</span> : null}
            </div>
          ))
        ) : unshown ? null : (
          <Empty title="No warning signs" />
        )}
        {unshown ? (
          <div className="li">
            <span className="dot" style={{ marginTop: 6 }} />
            <div className="b sub">{UNSHOWN_REASONS_LINE}</div>
          </div>
        ) : null}
      </div>
      <div className="sub" style={{ fontSize: 12, marginTop: 6 }}>
        Score {h.value} of 100 on {dayMonth(h.as_of)}. It starts at 100, and each {unshown ? 'signal' : 'line above'} moves it.
      </div>
    </>
  );
}
